import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, X, Image as ImageIcon, Sprout, ShoppingBag, Film, Play } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/apiBase';

const CATEGORIES = [
  { id: 'vegetables', name: 'Vegetables' },
  { id: 'fruits', name: 'Fruits' },
  { id: 'grains', name: 'Grains & Cereals' },
  { id: 'pulses', name: 'Pulses & Lentils' },
  { id: 'spices', name: 'Spices' },
  { id: 'dairy', name: 'Dairy' },
  { id: 'oilseeds', name: 'Oilseeds' },
  { id: 'other', name: 'Other' },
];

const UNITS = ['kg', 'quintal', 'ton', 'litre', 'dozen', 'piece', 'bundle'];

const LAST_POST_TYPE_KEY = 'frootex_last_post_type';

// Produce media caps (mirrored on backend)
const MAX_MEDIA_TOTAL = 6;
const MAX_VIDEOS = 2;

export default function CreatePostPage() {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user } = useAuth();
  const isEditing = !!editId;
  const isFarmer = user?.role === 'farmer';
  const isEditRequirement = isEditing && location.pathname.startsWith('/edit-requirement');

  // Post type: 'produce' (farmer listing) or 'requirement' (buyer wants to buy)
  const [postType, setPostType] = useState(() => {
    if (isEditRequirement) return 'requirement';
    if (isEditing) return 'produce';
    const stored = localStorage.getItem(LAST_POST_TYPE_KEY);
    if (stored === 'produce' || stored === 'requirement') return stored;
    return isFarmer ? 'produce' : 'requirement';
  });

  useEffect(() => {
    if (!isEditing) localStorage.setItem(LAST_POST_TYPE_KEY, postType);
  }, [postType, isEditing]);

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    quantity: '',
    unit: 'kg',
    price_per_unit: '',
    location: '',
  });
  const [images, setImages] = useState([]); // used for produce (multi) & requirement (single)
  const [videos, setVideos] = useState([]); // produce only, max 2
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEditing) return;
    const fetchPost = async () => {
      try {
        if (isEditRequirement) {
          const { data } = await api.get(`/requirements/${editId}`);
          setForm({
            title: data.title || '',
            description: data.description || '',
            category: data.category || '',
            quantity: String(data.quantity || ''),
            unit: data.unit || 'kg',
            price_per_unit: String(data.price_per_unit || ''),
            location: data.location || '',
          });
          setImages(data.image ? [data.image] : []);
        } else {
          const { data } = await api.get(`/produce/${editId}`);
          setForm({
            title: data.title || '',
            description: data.description || '',
            category: data.category || '',
            quantity: String(data.quantity || ''),
            unit: data.unit || 'kg',
            price_per_unit: String(data.price_per_unit || ''),
            location: data.location || '',
          });
          setImages(data.images || []);
          setVideos(data.videos || []);
        }
      } catch {
        toast.error('Failed to load post');
        navigate(isEditRequirement ? '/my-requirements' : '/farmer-dashboard');
      }
    };
    fetchPost();
  }, [isEditing, isEditRequirement, editId, api, navigate]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const isRequirement = postType === 'requirement';
  const totalMedia = images.length + videos.length;
  const canAddMore = isRequirement ? images.length < 1 : totalMedia < MAX_MEDIA_TOTAL;
  const canAddVideo = !isRequirement && videos.length < MAX_VIDEOS && totalMedia < MAX_MEDIA_TOTAL;

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = ''; // reset input immediately so retries work
    if (files.length === 0) return;
    const room = isRequirement ? (1 - images.length) : (MAX_MEDIA_TOTAL - totalMedia);
    if (room <= 0) {
      toast.error(isRequirement ? 'Only 1 image allowed' : `Max ${MAX_MEDIA_TOTAL} attachments (photos + videos)`);
      return;
    }
    const slice = files.slice(0, room);
    setUploading(true);
    try {
      for (const file of slice) {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const imageUrl = `${API_BASE}/api/files/${data.storage_path}`;
        setImages(prev => [...prev, imageUrl]);
      }
      toast.success('Photo uploaded');
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length === 0) return;
    const videoRoom = MAX_VIDEOS - videos.length;
    const totalRoom = MAX_MEDIA_TOTAL - totalMedia;
    const room = Math.min(videoRoom, totalRoom);
    if (room <= 0) {
      toast.error(videos.length >= MAX_VIDEOS ? `Max ${MAX_VIDEOS} videos` : `Max ${MAX_MEDIA_TOTAL} attachments`);
      return;
    }
    const slice = files.slice(0, room);
    setUploading(true);
    try {
      for (const file of slice) {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const url = `${API_BASE}/api/files/${data.storage_path}`;
        setVideos(prev => [...prev, url]);
      }
      toast.success('Video uploaded');
    } catch {
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));
  const removeVideo = (idx) => setVideos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.quantity || !form.price_per_unit) {
      toast.error('Fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (postType === 'produce') {
        if (!isFarmer && !isEditing) {
          toast.error('Only verified farmers can post produce. Apply for KYC first.');
          setSubmitting(false);
          return;
        }
        const payload = {
          ...form,
          quantity: parseFloat(form.quantity),
          price_per_unit: parseFloat(form.price_per_unit),
          images,
          videos,
        };
        if (isEditing) {
          await api.put(`/produce/${editId}`, payload);
          toast.success('Produce updated');
        } else {
          await api.post('/produce', payload);
          toast.success('Produce posted');
        }
        navigate('/farmer-dashboard');
      } else {
        // Requirement post
        const payload = {
          title: form.title,
          description: form.description,
          category: form.category,
          quantity: parseFloat(form.quantity),
          unit: form.unit,
          price_per_unit: parseFloat(form.price_per_unit),
          location: form.location,
          image: images[0] || null,
        };
        if (isEditRequirement) {
          await api.put(`/requirements/${editId}`, payload);
          toast.success('Requirement updated');
          navigate('/my-requirements');
        } else {
          await api.post('/requirements', payload);
          toast.success('Requirement posted');
          navigate('/');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell showNav={false}>
      <div className="p-4" data-testid="create-post-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-[#EFF2F7] flex items-center justify-center"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4 text-[#0B1620]" />
          </button>
          <h1 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">
            {isEditRequirement ? 'Edit requirement' : isEditing ? 'Edit Produce' : 'Create post'}
          </h1>
        </div>

        {/* Post type toggle (hidden while editing existing produce) */}
        {!isEditing && (
          <div
            role="tablist"
            data-testid="post-type-toggle"
            className="relative grid grid-cols-2 gap-1 p-1 bg-[#EFF2F7] rounded-full mb-5 border border-[#E4E8EF]"
          >
            <button
              type="button"
              role="tab"
              aria-selected={postType === 'produce'}
              data-testid="post-type-produce"
              onClick={() => setPostType('produce')}
              className={`relative z-10 h-10 rounded-full flex items-center justify-center gap-1.5 text-[12px] font-semibold transition-all duration-200 ${
                postType === 'produce'
                  ? 'bg-[#F6F8FB] text-[#0B1620] shadow-[0_2px_8px_-2px_rgba(11,22,32,0.08)]'
                  : 'text-[#5B6675] hover:text-[#0B1620]'
              }`}
            >
              <Sprout className="w-3.5 h-3.5" />
              Sell Produce
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={postType === 'requirement'}
              data-testid="post-type-requirement"
              onClick={() => setPostType('requirement')}
              className={`relative z-10 h-10 rounded-full flex items-center justify-center gap-1.5 text-[12px] font-semibold transition-all duration-200 ${
                postType === 'requirement'
                  ? 'bg-[#F6F8FB] text-[#0B1620] shadow-[0_2px_8px_-2px_rgba(11,22,32,0.08)]'
                  : 'text-[#5B6675] hover:text-[#0B1620]'
              }`}
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              Buy — Post Requirement
            </button>
          </div>
        )}

        {/* Non-farmer nudge on Produce tab */}
        {!isEditing && postType === 'produce' && !isFarmer && (
          <div
            data-testid="kyc-required-notice"
            className="mb-4 p-3 rounded-2xl bg-[#F6F8FB] border border-[#0B4F3F]/25 flex items-start gap-2"
          >
            <Sprout className="w-4 h-4 text-[#0B4F3F] mt-0.5 flex-shrink-0" />
            <div className="text-[12px] text-[#0B1620] leading-snug">
              Only verified farmers can list produce.
              <button
                type="button"
                onClick={() => navigate('/kyc-apply')}
                className="ml-1 text-[#0B4F3F] font-semibold underline underline-offset-2"
              >
                Complete KYC
              </button>
              {' '}or switch to a buyer requirement post.
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Media (Images + optional Videos for produce) */}
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">
              {isRequirement ? 'Photo (optional)' : 'Media'}
              <span className="ml-2 text-[10px] text-[#5B6675] font-normal">
                {isRequirement
                  ? 'Up to 1 photo'
                  : `Up to ${MAX_MEDIA_TOTAL} (max ${MAX_VIDEOS} videos) · ${totalMedia}/${MAX_MEDIA_TOTAL}`}
              </span>
            </Label>
            <div className="flex gap-2 flex-wrap">
              {images.map((img, i) => (
                <div key={img + i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#E4E8EF]">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center"
                    aria-label="Remove image"
                  >
                    <X className="w-3 h-3 text-[#F6F8FB]" />
                  </button>
                </div>
              ))}
              {videos.map((v, i) => (
                <div
                  key={v + i}
                  data-testid={`video-preview-${i}`}
                  className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#E4E8EF] bg-[#0B1620]"
                >
                  <video src={v} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-7 h-7 rounded-full bg-black/55 flex items-center justify-center">
                      <Play className="w-3.5 h-3.5 text-[#F6F8FB]" fill="currentColor" />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeVideo(i)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center"
                    aria-label="Remove video"
                  >
                    <X className="w-3 h-3 text-[#F6F8FB]" />
                  </button>
                </div>
              ))}
              {canAddMore && (
                <label
                  data-testid="upload-image-btn"
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-[#E4E8EF] flex flex-col items-center justify-center cursor-pointer hover:border-[#0B4F3F] transition-colors"
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#0B4F3F] border-t-transparent" />
                  ) : (
                    <>
                      <ImageIcon className="w-5 h-5 text-[#5B6675]" />
                      <span className="text-[9px] text-[#5B6675] mt-0.5">Photo</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    multiple={!isRequirement}
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              )}
              {canAddVideo && (
                <label
                  data-testid="upload-video-btn"
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-[#E4E8EF] flex flex-col items-center justify-center cursor-pointer hover:border-[#0B4F3F] transition-colors"
                >
                  {uploading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#0B4F3F] border-t-transparent" />
                  ) : (
                    <>
                      <Film className="w-5 h-5 text-[#5B6675]" />
                      <span className="text-[9px] text-[#5B6675] mt-0.5">Video</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">
              {isRequirement ? 'What do you need? *' : 'Title *'}
            </Label>
            <Input
              data-testid="title-input"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder={isRequirement ? 'e.g., Looking for A-grade Alphonso Mangoes' : 'e.g., Fresh Organic Tomatoes'}
              className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
            />
          </div>

          {/* Category */}
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Category *</Label>
            <Select value={form.category} onValueChange={(v) => handleChange('category', v)}>
              <SelectTrigger data-testid="category-select" className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620]">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-[#F6F8FB] border-[#E4E8EF]">
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Quantity + Unit */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Quantity *</Label>
              <Input
                data-testid="quantity-input"
                type="number"
                value={form.quantity}
                onChange={(e) => handleChange('quantity', e.target.value)}
                placeholder="500"
                className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Unit *</Label>
              <Select value={form.unit} onValueChange={(v) => handleChange('unit', v)}>
                <SelectTrigger data-testid="unit-select" className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#F6F8FB] border-[#E4E8EF]">
                  {UNITS.map(u => (
                    <SelectItem key={u} value={u}>{u}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Price */}
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">
              {isRequirement ? 'Your offer per unit (₹) *' : 'Price per unit (₹) *'}
            </Label>
            <Input
              data-testid="price-input"
              type="number"
              value={form.price_per_unit}
              onChange={(e) => handleChange('price_per_unit', e.target.value)}
              placeholder="40"
              className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">Description</Label>
            <Textarea
              data-testid="description-input"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={isRequirement ? 'Grade, packaging, delivery expectations…' : 'Describe your produce, quality, farming methods…'}
              rows={4}
              className="bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F] resize-none"
            />
          </div>

          {/* Location */}
          <div>
            <Label className="text-sm font-medium text-[#0B1620] mb-2 block">
              {isRequirement ? 'Delivery location' : 'Location'}
            </Label>
            <Input
              data-testid="location-input"
              value={form.location}
              onChange={(e) => handleChange('location', e.target.value)}
              placeholder="e.g., Punjab, India"
              className="h-11 bg-[#F1F5FA] border-[#E4E8EF] rounded-xl text-[#0B1620] placeholder:text-[#5B6675]/60 focus:ring-2 focus:ring-[#0B4F3F]/20 focus:border-[#0B4F3F]"
            />
          </div>

          {/* Submit */}
          <Button
            data-testid="submit-post-btn"
            type="submit"
            disabled={submitting || (postType === 'produce' && !isFarmer && !isEditing)}
            className="w-full h-12 bg-[#0B4F3F] hover:bg-[#083D31] text-[#F6F8FB] rounded-full font-semibold text-base transition-colors disabled:opacity-60"
          >
            {submitting
              ? 'Saving…'
              : isEditRequirement
                ? 'Update requirement'
                : isEditing
                  ? 'Update Produce'
                  : isRequirement
                    ? 'Post Requirement'
                    : 'Post Produce'}
          </Button>
        </form>
      </div>
    </AppShell>
  );
}
